import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
} from 'react';
import {
  Bold as BoldIcon,
  Clock3Icon,
  Italic as ItalicIcon,
  Link2 as LinkIcon,
  Strikethrough as StrikethroughIcon,
  List as BulletListIcon,
  ListOrdered as OrderedListIcon,
  RemoveFormatting as ClearFormatIcon,
  SendIcon,
  Underline as UnderlineIcon,
  XIcon,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ButtonGroup } from '@/components/ui/button-group';
import type { EmailTemplateRecord, Template } from '@/types/email';
import { EditorContent, useEditor } from '@tiptap/react';
import { Extension, Node, mergeAttributes } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Bold from '@tiptap/extension-bold';
import Link from '@tiptap/extension-link';
import { TextStyle } from '@tiptap/extension-text-style';
import Underline from '@tiptap/extension-underline';
import Strike from '@tiptap/extension-strike';
import FontFamily from '@tiptap/extension-font-family';
import Image from '@tiptap/extension-image';
import Paragraph from '@tiptap/extension-paragraph';
import { OFFICE_LOCATIONS, buildOfficeFooter, type OfficeLocation } from '@/constants/office-locations';

const formatPlaceholderToken = (placeholder: string) => `{{${placeholder.trim()}}}`;

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const renderPlaceholderBadge = (key: string, display: string) => {
  const token = formatPlaceholderToken(key);
  return `<span class="placeholder-badge inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 mx-0.5 text-xs font-semibold text-primary" data-placeholder-key="${escapeHtml(
    key
  )}" title="${escapeHtml(token)}" contenteditable="false">${escapeHtml(display)}</span>`;
};

const convertBadgesToTokens = (html: string) => {
  if (typeof document === 'undefined') {
    return html;
  }

  const container = document.createElement('div');
  container.innerHTML = html;
  container.querySelectorAll<HTMLElement>('[data-placeholder-key]').forEach((element) => {
    const key = element.getAttribute('data-placeholder-key')?.trim();
    const replacement = key ? formatPlaceholderToken(key) : element.textContent ?? '';
    const textNode = document.createTextNode(replacement);
    element.replaceWith(textNode);
  });

  return container.innerHTML;
};

const FontSize = Extension.create({
  name: 'fontSize',
  addGlobalAttributes() {
    return [
      {
        types: ['textStyle'],
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (element) => element.style.fontSize?.replace(/['"]/g, '') || null,
            renderHTML: (attributes) =>
              attributes.fontSize
                ? { style: `font-size: ${attributes.fontSize}` }
                : {},
          },
        },
      },
    ];
  },
  addCommands() {
    return {
      setFontSize:
        (fontSize: string) =>
        ({ chain }) =>
          chain().setMark('textStyle', { fontSize }).run(),
      unsetFontSize:
        () =>
        ({ chain }) =>
          chain().setMark('textStyle', { fontSize: null }).removeEmptyTextStyle().run(),
    };
  },
});

const ImageWithStyle = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      style: {
        default: null,
        parseHTML: (element) => element.getAttribute('style'),
        renderHTML: (attributes) =>
          attributes.style ? { style: attributes.style } : {},
      },
    };
  },
});

const ParagraphWithStyle = Paragraph.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      style: {
        default: null,
        parseHTML: (element) => element.getAttribute('style'),
        renderHTML: (attributes) => {
          const baseStyle = 'margin:0; padding:0;';
          if (!attributes.style) {
            return { style: baseStyle };
          }
          return { style: `${baseStyle} ${attributes.style}` };
        },
      },
    };
  },
  renderHTML({ HTMLAttributes, node }) {
    const isEmpty = node.content.size === 0;
    return [
      'p',
      HTMLAttributes,
      isEmpty ? '\u00A0' : 0,
    ];
  },
});

const BoldWithStyle = Bold.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      style: {
        default: null,
        parseHTML: (element) => element.getAttribute('style'),
        renderHTML: (attributes) =>
          attributes.style ? { style: attributes.style } : {},
      },
    };
  },
});

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    fontSize: {
      setFontSize: (fontSize: string) => ReturnType;
      unsetFontSize: () => ReturnType;
    };
  }
}

const PlaceholderBadge = Node.create({
  name: 'placeholderBadge',
  inline: true,
  group: 'inline',
  atom: true,
  selectable: false,
  addAttributes() {
    return {
      key: { default: null },
      title: { default: null },
      display: { default: null },
    };
  },
  parseHTML() {
    return [
      {
        tag: 'span[data-placeholder-key]',
        getAttrs: (node) => {
          if (!(node instanceof HTMLElement)) {
            return false;
          }
          return {
            key: node.getAttribute('data-placeholder-key'),
            title: node.getAttribute('title'),
            display: node.textContent ?? '',
          };
        },
      },
    ];
  },
  renderHTML({ HTMLAttributes }) {
    const key = HTMLAttributes.key ?? HTMLAttributes['data-placeholder-key'];
    const title = HTMLAttributes.title ?? (key ? `{{${key}}}` : null);
    const display = HTMLAttributes.display ?? HTMLAttributes['data-placeholder-display'];

    return [
      'span',
      mergeAttributes(HTMLAttributes, {
        'data-placeholder-key': key,
        class: 'placeholder-badge',
        contenteditable: 'false',
        title: title ?? '',
      }),
      display ?? key ?? '',
    ];
  },
});

export type SchedulingOption = 'one-hour' | 'tomorrow-morning';

export type EmailRecipient = {
  id: string;
  name: string;
  email: string;
  applicationId?: string | number | null;
  applicationKey?: string | null;
  candidateId?: string | null;
  vacancyId?: string | null;
  markers?: Record<string, string> | null;
  vacatureNaam?: string | null;
};

type EmailSendDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  recipients: EmailRecipient[];
  onRemoveRecipient: (recipientId: string) => void;
  templates: EmailTemplateRecord[];
  selectedTemplate: Template;
  onTemplateChange: (template: Template) => void;
  messageBody: string;
  onMessageBodyChange: (body: string) => void;
  subjectLine: string;
  onSubjectChange: (value: string) => void;
  onSend: () => void;
  onSchedule?: (option: SchedulingOption) => void | Promise<void>;
  onScheduleCustom?: (scheduledAt: Date) => void | Promise<void>;
  isSending?: boolean;
  errorMessage?: string | null;
  availableMarkers?: Record<string, string> | null;
  hideCompanyFallback?: boolean;
  location: OfficeLocation;
  onLocationChange: (location: OfficeLocation) => void;
  fromEmail: string;
  fromEmailOptions: string[];
  onFromEmailChange: (email: string) => void;
  fromEmailDisabled?: boolean;
};

export const EmailSendDialog = ({
  open,
  onOpenChange,
  recipients,
  onRemoveRecipient,
  templates,
  selectedTemplate,
  onTemplateChange,
  messageBody,
  onMessageBodyChange,
  subjectLine,
  onSubjectChange,
  onSend,
  isSending = false,
  onSchedule,
  onScheduleCustom,
  errorMessage = null,
  availableMarkers = null,
  hideCompanyFallback = false,
  location,
  onLocationChange,
  fromEmail,
  fromEmailOptions,
  onFromEmailChange,
  fromEmailDisabled = false,
}: EmailSendDialogProps) => {
  const resolvedFromEmail = fromEmail || fromEmailOptions[0] || 'office@siebertwassink.nl';
  const [view, setView] = useState<'compose' | 'schedule'>('compose');
  const [customScheduleVisible, setCustomScheduleVisible] = useState(false);
  const [customDate, setCustomDate] = useState('');
  const [customTime, setCustomTime] = useState('');
  const [customError, setCustomError] = useState<string | null>(null);
  const [selectedScheduleOption, setSelectedScheduleOption] =
    useState<SchedulingOption | null>(null);
  const [bodyViewMode, setBodyViewMode] = useState<'pretty' | 'raw'>('pretty');
  const [previewHtml, setPreviewHtml] = useState(messageBody);
  // const [tokenPickerResetKey, setTokenPickerResetKey] = useState(0);
  const [subjectInputFocused, setSubjectInputFocused] = useState(false);
  const [editorSelectionVersion, setEditorSelectionVersion] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const skipPreviewSyncRef = useRef(false);
  const editorUpdateRef = useRef(false);
  const [showAllRecipients, setShowAllRecipients] = useState(false);

  // const insertBadgeAtSelection = useCallback(
  //   (badgeHtml: string) => {
  //     if (typeof document === 'undefined') {
  //       return false;
  //     }

  //     getContentEditableElement()?.focus();
  //     const selection = document.getSelection();
  //     let range: Range | null = null;

  //     if (selection && selection.rangeCount > 0) {
  //       const candidate = selection.getRangeAt(0);
  //       if (isNodeWithinEditor(candidate.commonAncestorContainer)) {
  //         range = candidate;
  //       }
  //     }

  //     if (!range) {
  //       const saved = savedSelectionRef.current;
  //       if (saved && isNodeWithinEditor(saved.commonAncestorContainer)) {
  //         range = saved.cloneRange();
  //         selection?.removeAllRanges();
  //         selection?.addRange(range);
  //       }
  //     }

  //     if (!range || !selection) {
  //       return false;
  //     }

  //     const template = document.createElement('template');
  //     template.innerHTML = badgeHtml;
  //     const fragment = template.content;
  //     if (!fragment.firstChild) {
  //       return false;
  //     }

  //     range.deleteContents();
  //     const lastNode = fragment.lastChild;
  //     range.insertNode(fragment);
  //     const collapsedRange = document.createRange();
  //     if (lastNode) {
  //       collapsedRange.setStartAfter(lastNode);
  //     } else {
  //       collapsedRange.setStart(range.endContainer, range.endOffset);
  //     }
  //     collapsedRange.collapse(true);
  //     selection.removeAllRanges();
  //     selection.addRange(collapsedRange);
  //     savedSelectionRef.current = collapsedRange.cloneRange();
  //     return true;
  //   },
  //   [isNodeWithinEditor]
  // );


  const scheduleOptions: Array<{ id: SchedulingOption; label: string }> = useMemo(
    () => [
      { id: 'one-hour', label: 'Over 1 uur' },
      { id: 'tomorrow-morning', label: 'Morgenvroeg 9:00' },
    ],
    []
  );

  const effectiveMarkers = useMemo<Record<string, string> | null>(() => {
    const primaryRecipient = recipients[0];
    if (primaryRecipient?.markers && Object.keys(primaryRecipient.markers).length > 0) {
      return primaryRecipient.markers;
    }
    return availableMarkers ?? null;
  }, [availableMarkers, recipients]);

  const markerEntries = useMemo(() => {
    const entries = Object.entries(effectiveMarkers ?? {});
    const sanitized: Array<[string, string]> = [];
    const allowedKeys = new Set([
      'firstName',
      'lastName',
      'recipientName',
      'jobTitle',
      'company',
      'companyLocation',
      'phoneNumber',
      'tel',
      'reception',
      'vacancyName',
      'senderEmail',
      'Locatie',
      'Advisor',
      'AdvisorOrOffice',
      'officeFooter',
    ]);
    for (const [rawKey, rawValue] of entries) {
      const key = (rawKey ?? '').toString().trim();
      const value = (rawValue ?? '').toString().trim();
      if (!key || !value) {
        continue;
      }
      if (!allowedKeys.has(key)) {
        continue;
      }
      sanitized.push([key, value]);
    }
    sanitized.sort((a, b) => a[0].localeCompare(b[0]));
    return sanitized;
  }, [effectiveMarkers]);

  const defaultPreviewValues = useMemo(() => {
    const primaryRecipient = recipients[0] ?? null;
    const officeDetails = OFFICE_LOCATIONS[location];
    const fullName = primaryRecipient?.name ?? 'Kandidaat';
    const [firstName = fullName, ...rest] = fullName.split(' ');
    const lastName = rest.join(' ').trim() || 'Achternaam';
    const candidateEmail = primaryRecipient?.email ?? 'kandidaat@example.com';

    return {
      firstName,
      lastName,
      recipientName: fullName,
      jobTitle: 'Functietitel',
      role: 'Functietitel',
      company: hideCompanyFallback ? '' : 'Siebert & Wassink',
      companyLocation: '',
      phoneNumber: officeDetails.phone,
      tel: officeDetails.tel,
      reception: officeDetails.reception,
      vacancyName: 'Vacaturetitel',
      senderEmail: resolvedFromEmail,
      candidateEmail,
      Locatie: officeDetails.address,
      Advisor: '',
      AdvisorOrOffice: '',
      officeFooter: buildOfficeFooter(location),
    };
  }, [resolvedFromEmail, hideCompanyFallback, location, recipients]);

  const markerOptions = useMemo(() => {
    const priorities = [
      'firstName',
      'lastName',
      'recipientName',
      'jobTitle',
      'company',
      'companyLocation',
      'phoneNumber',
      'tel',
      'reception',
      'vacancyName',
      'senderEmail',
      'Locatie',
      'Advisor',
      'AdvisorOrOffice',
      'officeFooter',
    ];

    const combined = new Map<string, string>();
    const markerSource = effectiveMarkers ?? {};

    for (const key of priorities) {
      const fallback = (defaultPreviewValues as Record<string, string>)[key] ?? '';
      const value = (markerSource[key] ?? fallback ?? '').toString().trim();
      if (!value) {
        continue;
      }
      combined.set(key, value);
    }

    for (const [key, value] of markerEntries) {
      if (!combined.has(key)) {
        combined.set(key, value);
      }
    }

    // Ensure AdvisorOrOffice follows the same precedence as send pipeline:
    // office sender override > explicit marker > Advisor value
    const normalizedFrom = resolvedFromEmail.toString().trim().toLowerCase();
    const hasExplicitAdvisorOrOffice =
      combined.has('AdvisorOrOffice') && (combined.get('AdvisorOrOffice') ?? '').toString().trim().length > 0;
    const hasAdvisor = combined.has('Advisor') && (combined.get('Advisor') ?? '').toString().trim().length > 0;
    if (normalizedFrom === 'office@siebertwassink.nl') {
      combined.set('AdvisorOrOffice', 'Team office');
    } else if (!hasExplicitAdvisorOrOffice && hasAdvisor) {
      combined.set('AdvisorOrOffice', combined.get('Advisor') ?? '');
    }

    return Array.from(combined.entries()).map(([key, value]) => ({
      key,
      value: key,
      preview: value,
    }));
  }, [defaultPreviewValues, effectiveMarkers, markerEntries, resolvedFromEmail]);

  const fontFamilyOptions = useMemo(
    () => [
      { label: 'System', value: 'default' },
      { label: 'Georgia', value: 'Georgia, serif' },
      { label: 'Times New Roman', value: '"Times New Roman", Times, serif' },
      { label: 'Helvetica', value: 'Helvetica, Arial, sans-serif' },
      { label: 'Verdana', value: 'Verdana, Geneva, sans-serif' },
      { label: 'Courier New', value: '"Courier New", Courier, monospace' },
    ],
    []
  );

  const fontSizeOptions = useMemo(
    () => [
      { label: '12px', value: '12px' },
      { label: '14px', value: '14px' },
      { label: '16px', value: '16px' },
      { label: '18px', value: '18px' },
      { label: '20px', value: '20px' },
      { label: '24px', value: '24px' },
      { label: '28px', value: '28px' },
    ],
    []
  );

  const previewPlaceholderValues = useMemo<Record<string, string>>(() => {
    const base = markerEntries.length === 0 ? { ...defaultPreviewValues } : markerEntries.reduce<Record<string, string>>((acc, [key, value]) => {
      acc[key] = value;
      return acc;
    }, { ...defaultPreviewValues });

    // Apply same precedence as send pipeline for previews
    const normalizedFrom = resolvedFromEmail.toString().trim().toLowerCase();
    const explicitAdvisorOrOffice = (base['AdvisorOrOffice'] ?? '').toString().trim();
    const explicitAdvisor = (base['Advisor'] ?? '').toString().trim();
    if (normalizedFrom === 'office@siebertwassink.nl') {
      base['AdvisorOrOffice'] = 'Team office';
    } else if (!explicitAdvisorOrOffice && explicitAdvisor) {
      base['AdvisorOrOffice'] = explicitAdvisor;
    }

    return base;
  }, [defaultPreviewValues, markerEntries, resolvedFromEmail]);

  const hasCompanyLocationMarker =
    typeof effectiveMarkers?.companyLocation === 'string' &&
    effectiveMarkers.companyLocation.trim().length > 0;
  const isFromEmailReadOnly = fromEmailDisabled || fromEmailOptions.length <= 1;

  const convertTokensToBadgeHtml = useCallback(
    (body: string) => {
      let processed = body;

      if (hideCompanyFallback) {
        processed = processed.replace(/\s*\bbij\s*\{\{\s*company\s*\}\}(\.)?/gi, (_m, dot) => (dot ? '.' : ''));
        processed = processed.replace(/\s{2,}/g, ' ').trim();
      }

      return processed.replace(/\{\{\s*([^}]+?)\s*\}\}/g, (match, rawKey, offset, source) => {
        const normalizedKey = (rawKey ?? '').trim();
        const displayValue = previewPlaceholderValues[normalizedKey] ?? normalizedKey;
        const previousChar = offset > 0 ? source[offset - 1] : '';
        const nextChar = source[offset + match.length] ?? '';
        const inAttributeContext =
          previousChar === '"' || previousChar === "'" || nextChar === '"' || nextChar === "'";

        if (inAttributeContext) {
          return escapeHtml(displayValue);
        }

        return renderPlaceholderBadge(normalizedKey, displayValue);
      });
    },
    [previewPlaceholderValues, hideCompanyFallback]
  );

  const renderSubjectMarkers = useCallback(
    (input: string) =>
      input.replace(/\{\{\s*([^}]+?)\s*\}\}/g, (match, rawKey) => {
        const normalizedKey = (rawKey ?? '').trim();
        const value = previewPlaceholderValues[normalizedKey];
        return typeof value === 'string' && value.length > 0 ? value : normalizedKey || match;
      }),
    [previewPlaceholderValues]
  );

  const subjectHasMarkers = useMemo(() => /\{\{\s*[^}]+?\s*\}\}/.test(subjectLine), [subjectLine]);
  const renderedSubjectPreview = useMemo(
    () => renderSubjectMarkers(subjectLine),
    [renderSubjectMarkers, subjectLine]
  );
  const subjectDisplayValue =
    subjectHasMarkers && !subjectInputFocused ? renderedSubjectPreview : subjectLine;

  useEffect(() => {
    if (skipPreviewSyncRef.current) {
      skipPreviewSyncRef.current = false;
      return;
    }
    const replaced = convertTokensToBadgeHtml(messageBody);
    setPreviewHtml(replaced);
  }, [convertTokensToBadgeHtml, messageBody]);

  const syncBodyToEditor = useCallback(
    (body: string) => {
      skipPreviewSyncRef.current = true;
      setPreviewHtml(convertTokensToBadgeHtml(body));
      onMessageBodyChange(body);
    },
    [convertTokensToBadgeHtml, onMessageBodyChange]
  );

  const applyEditorHtmlUpdate = useCallback(
    (html: string | null | undefined) => {
      if (typeof html !== 'string') {
        return;
      }
      const body = convertBadgesToTokens(html);
      syncBodyToEditor(body);
    },
    [syncBodyToEditor]
  );

  // const handlePlaceholderSelect = (value: string) => {
  //   setTokenPickerResetKey((key) => key + 1);
  //   const token = formatPlaceholderToken(value);
  //   const markerPreview =
  //     markerOptions.find((option) => option.key === value)?.preview ?? previewPlaceholderValues[value] ?? value;

  //   if (bodyViewMode === 'pretty') {
  //     const badgeHtml = renderPlaceholderBadge(value, markerPreview);
  //     const inserted = insertBadgeAtSelection(badgeHtml);
  //     if (inserted) {
  //       window.setTimeout(() => {
  //         const html = getContentEditableElement()?.innerHTML;
  //         if (typeof html === 'string') {
  //           applyEditorHtmlUpdate(html);
  //         }
  //       }, 0);
  //       return;
  //     }
  //   }

  //   if (bodyViewMode === 'raw') {
  //     const spacing = messageBody && !messageBody.endsWith(' ') ? ' ' : '';
  //     const updatedBody = `${messageBody}${spacing}${token}`;
  //     syncBodyToEditor(updatedBody);
  //     return;
  //   } else if (navigator.clipboard?.writeText) {
  //     navigator.clipboard
  //       .writeText(token)
  //       .catch(() => null);
  //   } else {
  //     // no-op fallback
  //   }
  // };

  const handleTemplateChange = (value: string) => {
    const nextTemplate = value as Template;
    onTemplateChange(nextTemplate);

    const matchedTemplate = templates.find(
      (template) => template.template === nextTemplate
    );
    if (matchedTemplate) {
      onMessageBodyChange(matchedTemplate.body);
      onSubjectChange(matchedTemplate.subject ?? '');
    }
  };

  const handleTextareaChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    onMessageBodyChange(event.target.value);
  };

  const handleSubjectInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    onSubjectChange(event.target.value);
  };

  const handleSubjectInputFocus = () => {
    setSubjectInputFocused(true);
  };

  const handleSubjectInputBlur = () => {
    setSubjectInputFocused(false);
  };

  const handleOpenSchedule = () => {
    setView('schedule');
  };

  const handleBackToCompose = () => {
    setView('compose');
    setSelectedScheduleOption(null);
    setCustomScheduleVisible(false);
    setCustomDate('');
    setCustomTime('');
    setCustomError(null);
  };

  useEffect(() => {
    if (!open) {
      setView('compose');
      setCustomScheduleVisible(false);
      setCustomDate('');
      setCustomTime('');
      setCustomError(null);
      setSelectedScheduleOption(null);
      setBodyViewMode('pretty');
      setPreviewHtml(convertTokensToBadgeHtml(messageBody));
      setSubjectInputFocused(false);
      setShowAllRecipients(false);
      return;
    }
    setPreviewHtml(convertTokensToBadgeHtml(messageBody));
  }, [convertTokensToBadgeHtml, messageBody, open]);

  useEffect(() => {
    if (recipients.length <= 3) {
      setShowAllRecipients(false);
    }
  }, [recipients.length]);

  useEffect(() => {
    if (bodyViewMode === 'raw') {
      requestAnimationFrame(() => {
        textareaRef.current?.focus();
      });
    }
  }, [bodyViewMode]);

  const handleEditorChange = useCallback(
    (content: string) => {
      editorUpdateRef.current = true;
      applyEditorHtmlUpdate(content);
    },
    [applyEditorHtmlUpdate]
  );

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        paragraph: false,
        bold: false,
      }),
      BoldWithStyle,
      ParagraphWithStyle,
      Underline,
      Strike,
      TextStyle,
      FontFamily,
      FontSize,
      ImageWithStyle.configure({
        inline: false,
        allowBase64: true,
      }),
      Link.configure({
        openOnClick: false,
        linkOnPaste: true,
        HTMLAttributes: {
          rel: 'noopener noreferrer',
          target: '_blank',
        },
      }),
      PlaceholderBadge,
    ],
    content: previewHtml,
    editorProps: {
      attributes: {
        class:
          'tiptap h-full w-full overflow-y-auto bg-transparent text-sm leading-relaxed focus:outline-none',
      },
    },
    onUpdate: ({ editor: currentEditor }) => {
      handleEditorChange(currentEditor.getHTML());
    },
    onTransaction: () => {
      setEditorSelectionVersion((current) => current + 1);
    },
    onSelectionUpdate: () => {
      setEditorSelectionVersion((current) => current + 1);
    },
  });

  useEffect(() => {
    if (!editor || bodyViewMode !== 'pretty') {
      return;
    }
    if (editorUpdateRef.current) {
      editorUpdateRef.current = false;
      return;
    }
    const current = editor.getHTML();
    if (current !== previewHtml) {
      editor.commands.setContent(previewHtml, { emitUpdate: false });
    }
  }, [bodyViewMode, editor, previewHtml]);

  const isEditorReady = Boolean(editor);
  const activeFontFamily = useMemo(
    () => (editor?.getAttributes('textStyle')?.fontFamily as string | null) ?? null,
    [editor, editorSelectionVersion]
  );
  const activeFontSize = useMemo(
    () => (editor?.getAttributes('textStyle')?.fontSize as string | null) ?? null,
    [editor, editorSelectionVersion]
  );
  const fontFamilyValue = activeFontFamily ?? 'default';
  const fontSizeValue = activeFontSize ?? '14px';
  const handleFontFamilyChange = useCallback(
    (value: string) => {
      if (!editor) {
        return;
      }
      if (value === 'default') {
        editor.chain().focus().unsetFontFamily().run();
        return;
      }
      editor.chain().focus().setFontFamily(value).run();
    },
    [editor]
  );

  const handleFontSizeChange = useCallback(
    (value: string) => {
      if (!editor) {
        return;
      }
      editor.chain().focus().setFontSize(value).run();
    },
    [editor]
  );

  const handleLinkAction = useCallback(() => {
    if (!editor) {
      return;
    }
    const previousUrl = editor.getAttributes('link').href ?? '';
    const url = window.prompt('URL', previousUrl);
    if (url === null) {
      return;
    }
    if (!url.trim()) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url.trim() }).run();
  }, [editor]);

  const handleSelectScheduleOption = (option: SchedulingOption) => {
    setSelectedScheduleOption((previous) =>
      previous === option ? null : option
    );
    setCustomScheduleVisible(false);
    setCustomError(null);
  };

  const handleShowCustomSchedule = () => {
    setCustomScheduleVisible(true);
    setSelectedScheduleOption(null);
    setCustomError(null);
  };

  const handleCustomDateChange = (event: ChangeEvent<HTMLInputElement>) => {
    setCustomDate(event.target.value);
    setCustomError(null);
  };

  const handleCustomTimeChange = (event: ChangeEvent<HTMLInputElement>) => {
    setCustomTime(event.target.value);
    setCustomError(null);
  };

  const handleConfirmSchedule = async () => {
    if (customScheduleVisible) {
      if (!customDate || !customTime) {
        setCustomError('Selecteer zowel een datum als tijdstip.');
        return;
      }

      const scheduledAt = new Date(`${customDate}T${customTime}`);
      if (Number.isNaN(scheduledAt.getTime())) {
        setCustomError('De gekozen datum of tijd is ongeldig.');
        return;
      }

      try {
        await Promise.resolve(onScheduleCustom?.(scheduledAt));
      } catch {
        return;
      }
    } else if (selectedScheduleOption) {
      try {
        await Promise.resolve(onSchedule?.(selectedScheduleOption));
      } catch {
        return;
      }
    } else {
      return;
    }

    setView('compose');
    onOpenChange(false);
    setSelectedScheduleOption(null);
    setCustomScheduleVisible(false);
    setCustomDate('');
    setCustomTime('');
    setCustomError(null);
  };

  const canConfirmSchedule = customScheduleVisible
    ? Boolean(customDate && customTime)
    : Boolean(selectedScheduleOption);

  const hasRecipientOverflow = recipients.length > 3;
  const displayedRecipients = showAllRecipients || !hasRecipientOverflow
    ? recipients
    : recipients.slice(0, 3);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl bg-white border-none rounded-lg max-h-[calc(100vh-48px)] max-w-[calc(100vw-48px)] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className='text-3xl'>Mail Versturen</DialogTitle>
          <DialogDescription className='text-slate-500'>
            Automatische email fucntionaliteit
          </DialogDescription>
        </DialogHeader>

        {view === 'compose' ? (
          <div className="flex flex-col gap-6">
            <section aria-label="Ontvangers" className="space-y-3">
              <h3 className="text-xl font-semibold text-black">
                Geselecteerd ({recipients.length})
              </h3>
              {recipients.length === 0 ? (
                <p className="text-sm text-slate-500">
                  Geen ontvangers geselecteerd.
                </p>
              ) : (
                <>
                  {showAllRecipients ? (
                    <div className="rounded-md border border-slate-200 bg-white">
                      <ScrollArea className="h-60 w-full pr-2">
                        <ul className="flex flex-col divide-y divide-slate-100">
                          {displayedRecipients.map((recipient) => (
                            <li
                              key={recipient.id}
                              className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
                            >
                              <div className="flex flex-col">
                                <span className="font-medium text-slate-900">
                                  {recipient.name}
                                </span>
                                <span className="text-slate-500">{recipient.email}</span>
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon-sm"
                                onClick={() => onRemoveRecipient(recipient.id)}
                                aria-label={`Ontvanger ${recipient.name} verwijderen`}
                              >
                                <XIcon className="h-4 w-4" />
                              </Button>
                            </li>
                          ))}
                        </ul>
                      </ScrollArea>
                    </div>
                  ) : (
                    <ul className="flex flex-col gap-2">
                      {displayedRecipients.map((recipient) => (
                        <li
                          key={recipient.id}
                          className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2 text-sm"
                        >
                          <div className="flex flex-col">
                            <span className="font-medium text-slate-900">
                              {recipient.name}
                            </span>
                            <span className="text-slate-500">{recipient.email}</span>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-sm"
                            onClick={() => onRemoveRecipient(recipient.id)}
                            aria-label={`Ontvanger ${recipient.name} verwijderen`}
                          >
                            <XIcon className="h-4 w-4" />
                          </Button>
                        </li>
                      ))}
                    </ul>
                  )}
                  {!showAllRecipients && hasRecipientOverflow ? (
                    <Button
                      type="button"
                      variant="ghost"
                      className="text-primary px-0"
                      onClick={() => setShowAllRecipients(true)}
                    >
                      Alles bekijken
                    </Button>
                  ) : null}
                </>
              )}
            </section>

            <section aria-label="Emailtemplate" className="space-y-3">
              <div className="flex flex-col gap-2">
                <h3 className='font-semibold text-xl'> Email Template</h3>
                <Select
                  value={selectedTemplate}
                  onValueChange={handleTemplateChange}
                >
                  <SelectTrigger id="email-template-select" aria-label="Selecteer een emailtemplate" className="h-10 rounded-md border border-slate-200 px-3 text-sm font-normal text-slate-900 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:bg-slate-500/10 disabled:opacity-100">
                    <SelectValue placeholder="Kies een template" />
                  </SelectTrigger>
                  <SelectContent align='start'>
                    {templates.map((template) => (
                      <SelectItem key={template.template} value={template.template}>
                        {template.template}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col gap-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <span className="text-sm font-medium text-slate-600">Email inhoud</span>
                  <div className="flex flex-wrap items-center gap-2">
                    <ButtonGroup>
                      <Button
                        type="button"
                        variant={ bodyViewMode === 'pretty' ? "default" : "outline" }
                        className={`px-3 py-1 h-7 transition`}
                        onClick={() => setBodyViewMode('pretty')}
                      >
                        Editor
                      </Button>
                      <Button
                       type="button"
                       variant={ bodyViewMode === 'raw' ? "default" : "outline" }
                        className={`px-3 py-1 h-7 transition`}
                        onClick={() => setBodyViewMode('raw')}
                      >
                        Bron
                      </Button>
                    </ButtonGroup>
                    {/* {markerOptions.length > 0 ? (
                      <ButtonGroup>
                        <Select key={tokenPickerResetKey} onValueChange={handlePlaceholderSelect}>
                          <SelectTrigger
                            className="h-8 min-w-[180px] rounded-md text-sm"
                            aria-label="Token toevoegen"
                            onMouseDown={handleTokenPickerPointerDown}
                          >
                            <SelectValue placeholder="Token toevoegen" />
                          </SelectTrigger>
                          <SelectContent position="popper" className="max-h-64">
                            <ScrollArea className="max-h-64">
                              <div className="flex flex-col gap-1 pr-2">
                                {markerOptions.map((option) => (
                                  <SelectItem
                                    key={option.key}
                                    value={option.value}
                                    className="flex w-full flex-col items-start gap-0.5"
                                  >
                                    <span className="font-medium">
                                      {formatPlaceholderToken(option.value)}
                                    </span>
                                    <span className="max-w-full text-xs text-slate-500">
                                      {option.preview}
                                    </span>
                                  </SelectItem>
                                ))}
                              </div>
                            </ScrollArea>
                          </SelectContent>
                        </Select>
                      </ButtonGroup>
                    ) : null} */}
                  </div>                  
                </div>

                {markerOptions.length === 0 ? (
                  <span className="text-xs text-end text-slate-500">
                    Geen dynamische markers beschikbaar voor deze kandidaat.
                  </span>
                ) : null}
                {!hideCompanyFallback && !hasCompanyLocationMarker ? (
                  <span className="text-xs text-end text-amber-600">
                    Geen HubSpot-adres voor deze opdrachtgever.
                  </span>
                ) : null}

                <div className="flex flex-wrap items-center justify-between gap-3">
                  <label className="text-sm font-medium text-slate-600">
                    Verzenden vanaf
                  </label>
                  {isFromEmailReadOnly ? (
                    <input
                      type="text"
                      value={resolvedFromEmail}
                      disabled
                      className="h-10 w-full rounded-md border bg-slate-500/10 border-slate-200 px-3 text-sm text-slate-900 opacity-100"
                    />
                  ) : (
                    <Select
                      value={fromEmail}
                      onValueChange={onFromEmailChange}
                    >
                      <SelectTrigger
                        className="h-10 rounded-md border border-slate-200 px-3 text-sm font-normal text-slate-900 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-1"
                        aria-label="Selecteer afzender"
                      >
                        <SelectValue placeholder="Kies afzender" />
                      </SelectTrigger>
                      <SelectContent align="start">
                        {fromEmailOptions.map((emailOption) => (
                          <SelectItem key={emailOption} value={emailOption}>
                            {emailOption}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <span className="text-sm font-medium text-slate-600">Locatie</span>
                  <div className="flex flex-wrap items-center gap-2">
                    <ButtonGroup>
                      <Button
                        type="button"
                        variant={ location === 'Arnhem' ? "default" : "outline" }
                        className={`px-3 py-1 h-7 transition`}
                        onClick={() => onLocationChange('Arnhem')}
                      >
                        Arnhem
                      </Button>
                      <Button
                       type="button"
                       variant={ location === 'Hengelo' ? "default" : "outline" }
                        className={`px-3 py-1 h-7 transition`}
                        onClick={() => onLocationChange('Hengelo')}
                      >
                        Hengelo
                      </Button>
                      <Button
                        type="button"
                        variant={ location === 'Zwolle' ? "default" : "outline" }
                        className={`px-3 py-1 h-7 transition`}
                        onClick={() => onLocationChange('Zwolle')}
                      >
                        Zwolle
                      </Button>
                    </ButtonGroup>
                  </div>
                </div>

                <div className="flex flex-col gap-1">
                  <label htmlFor="email-template-subject" className="text-sm font-medium text-slate-600">
                    Onderwerp
                  </label>
                  <input
                    id="email-template-subject"
                    type="text"
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
                    value={subjectDisplayValue}
                    onChange={handleSubjectInputChange}
                    onFocus={handleSubjectInputFocus}
                    onBlur={handleSubjectInputBlur}
                    placeholder="Onderwerp van de e-mail"
                  />
                </div>

                <div className="rounded-md border border-slate-200 bg-white">
                  {bodyViewMode === 'pretty' ? (
                    <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 px-2 py-2">
                      <Select
                        value={fontFamilyValue}
                        onValueChange={handleFontFamilyChange}
                        disabled={!isEditorReady}
                      >
                        <SelectTrigger
                          className="h-8 w-auto  rounded-md border border-slate-200 px-2 text-xs"
                          aria-label="Lettertype kiezen"
                        >
                          <SelectValue placeholder="Lettertype" />
                        </SelectTrigger>
                        <SelectContent align="start">
                          {fontFamilyOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      <Select
                        value={fontSizeValue}
                        onValueChange={handleFontSizeChange}
                        disabled={!isEditorReady}
                      >
                        <SelectTrigger
                          className="h-8 w-auto  rounded-md border border-slate-200 px-2 text-xs"
                          aria-label="Lettergrootte kiezen"
                        >
                          <SelectValue placeholder="Grootte" />
                        </SelectTrigger>
                        <SelectContent align="start">
                          {fontSizeOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      <ButtonGroup className=" shrink-0">
                        <Button
                          type="button"
                          variant={editor?.isActive('bold') ? 'default' : 'outline'}
                          size="icon-sm"
                          onClick={() => editor?.chain().focus().toggleBold().run()}
                          disabled={!isEditorReady}
                          aria-label="Vet"
                        >
                          <BoldIcon className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant={editor?.isActive('italic') ? 'default' : 'outline'}
                          size="icon-sm"
                          onClick={() => editor?.chain().focus().toggleItalic().run()}
                          disabled={!isEditorReady}
                          aria-label="Cursief"
                        >
                          <ItalicIcon className="h-4 w-4" />
                        </Button>
                      <Button
                        type="button"
                        variant={editor?.isActive('underline') ? 'default' : 'outline'}
                        size="icon-sm"
                        onClick={() => editor?.chain().focus().toggleUnderline().run()}
                        disabled={!isEditorReady}
                        aria-label="Onderstrepen"
                      >
                        <UnderlineIcon className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        variant={editor?.isActive('strike') ? 'default' : 'outline'}
                        size="icon-sm"
                        onClick={() => editor?.chain().focus().toggleStrike().run()}
                        disabled={!isEditorReady}
                        aria-label="Doorhalen"
                      >
                        <StrikethroughIcon className="h-4 w-4" />
                      </Button>
                    </ButtonGroup>

                      <ButtonGroup className=" shrink-0">
                        <Button
                          type="button"
                          variant={editor?.isActive('bulletList') ? 'default' : 'outline'}
                          size="icon-sm"
                          onClick={() => editor?.chain().focus().toggleBulletList().run()}
                          disabled={!isEditorReady}
                          aria-label="Opsomming"
                        >
                          <BulletListIcon className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant={editor?.isActive('orderedList') ? 'default' : 'outline'}
                          size="icon-sm"
                          onClick={() => editor?.chain().focus().toggleOrderedList().run()}
                          disabled={!isEditorReady}
                          aria-label="Genummerde lijst"
                        >
                          <OrderedListIcon className="h-4 w-4" />
                        </Button>
                      </ButtonGroup>

                      <ButtonGroup className=" shrink-0">
                        <Button
                          type="button"
                          variant={editor?.isActive('link') ? 'default' : 'outline'}
                          size="icon-sm"
                          onClick={handleLinkAction}
                          disabled={!isEditorReady}
                          aria-label="Link"
                        >
                          <LinkIcon className="h-4 w-4" />
                        </Button>
                      </ButtonGroup>

                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="ml-auto h-8 shrink-0 gap-2 text-xs"
                        onClick={() => editor?.chain().focus().unsetAllMarks().clearNodes().run()}
                        disabled={!isEditorReady}
                      >
                        <ClearFormatIcon className="h-4 w-4" />
                        Clear formatting
                      </Button>
                    </div>
                  ) : null}

                  <div className="email-editor relative h-[300px] w-full overflow-y-auto">
                    {bodyViewMode === 'pretty' ? (
                      <EditorContent editor={editor} />
                    ) : (
                      <textarea
                        id="email-template-body"
                        className="h-full w-full resize-none border-0 bg-transparent px-3 py-2 text-sm leading-relaxed focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:bg-slate-50"
                        value={messageBody}
                        onChange={handleTextareaChange}
                        ref={textareaRef}
                      />
                    )}
                  </div>
                </div>
                {errorMessage ? (
                  <p className="text-sm text-red-600">{errorMessage}</p>
                ) : null}
              </div>
            </section>
          </div>
        ) : (
          <div className="flex flex-col gap-6">
            <section className="space-y-3">
              <h3 className="text-xl font-semibold text-black">
                Email inplannen
              </h3>
              <p className="text-sm text-slate-500">
                Kies wanneer de email verzonden moet worden.
              </p>
              <div className="grid gap-3 sm:grid-cols-3">
                {scheduleOptions.map((option) => (
                  <Button
                    key={option.id}
                    type="button"
                    variant="outline"
                    className={`flex h-24 flex-col items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white text-sm font-medium text-slate-700 hover:border-primary ${selectedScheduleOption === option.id
                        ? 'border-primary bg-primary/10 text-primary'
                        : ''
                      }`}
                    aria-pressed={selectedScheduleOption === option.id}
                    onClick={() => handleSelectScheduleOption(option.id)}
                  >
                    <Clock3Icon className="h-5 w-5" />
                    {option.label}
                  </Button>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  className={`flex h-24 flex-col items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white text-sm font-medium text-slate-700 hover:border-primary ${customScheduleVisible
                      ? 'border-primary bg-primary/10 text-primary'
                      : ''
                    }`}
                  aria-pressed={customScheduleVisible}
                  onClick={handleShowCustomSchedule}
                >
                  <Clock3Icon className="h-5 w-5" />
                  Aangepast
                </Button>
              </div>
              {customScheduleVisible && (
                <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="flex flex-col gap-4">
                    <p className="text-sm text-slate-600">
                      Kies een specifieke datum en tijd om de email automatisch te versturen.
                    </p>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <label className="flex flex-col gap-1 text-sm font-medium text-slate-700">
                        Datum
                        <input
                          type="date"
                          value={customDate}
                          onChange={handleCustomDateChange}
                          className="rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                          min={new Date().toISOString().split('T')[0]}
                        />
                      </label>
                      <label className="flex flex-col gap-1 text-sm font-medium text-slate-700">
                        Tijd
                        <input
                          type="time"
                          value={customTime}
                          onChange={handleCustomTimeChange}
                          className="rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </label>
                    </div>
                    {customError && (
                      <p className="text-sm text-red-600">{customError}</p>
                    )}
                  </div>
                </div>
              )}
            </section>
          </div>
        )}

        <DialogFooter>
          {view === 'compose' ? (
            <>
              <Button
                type="button"
                variant="outline"
                style="pill"
                onClick={handleOpenSchedule}
                disabled={isSending || recipients.length === 0}
              >
                Email inplannen
              </Button>
              <Button
                type="button"
                onClick={onSend}
                style="pill"
                disabled={isSending || recipients.length === 0}
              >
                {!isSending && <SendIcon className="h-4 w-4" />}
                {isSending ? 'Versturen...' : 'Versturen'}
              </Button>
            </>
          ) : (
            <>
              <Button
                type="button"
                variant="outline"
                style="pill"
                onClick={handleBackToCompose}
              >
                Terug
              </Button>
              <Button
                type="button"
                style="pill"
                onClick={handleConfirmSchedule}
                disabled={
                  isSending || recipients.length === 0 || !canConfirmSchedule
                }
              >
                Versturen
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
